import { differenceInDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { TIMELINE_ROW_AXIS_LABEL_WIDTH } from './layout/TimelineRowAxisLabel';

export const DEFAULT_ROW_HEADER_WIDTH = 120;
export const VERTICAL_ROW_HEADER_WIDTH = 32;
export const PARENT_HEADER_HEIGHT = 32;
export const TIMELINE_HEADER_HEIGHT = 70;

export const RESIZE_HANDLE_WIDTH = 4;
export const RESIZE_HANDLE_HITBOX_PADDING = 8;
export const RESIZE_HANDLE_CENTER_OFFSET = 2;

export const useTimelineColumnDayWidth = ({
  scrollContainerRef,
  hasVerticalSubHeaders,
  from,
  to,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  hasVerticalSubHeaders: boolean;
  from: string;
  to: string;
  rowHeaderWidth?: number;
}) => {
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.target.clientWidth);
      }
    });

    resizeObserver.observe(element);
    setContainerWidth(element.clientWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  return useMemo(() => {
    const timelineWidth = hasVerticalSubHeaders
      ? containerWidth - rowHeaderWidth - PARENT_HEADER_HEIGHT - TIMELINE_ROW_AXIS_LABEL_WIDTH
      : containerWidth - rowHeaderWidth - TIMELINE_ROW_AXIS_LABEL_WIDTH;
    const bufferedWidth = timelineWidth - 10; // Buffer to prevent horizontal scrollbar
    const totalDays = differenceInDays(new Date(to), new Date(from)) + 1;
    if (totalDays <= 31) {
      return Math.floor(bufferedWidth / totalDays);
    }

    const dayWidth = bufferedWidth / totalDays;
    return dayWidth;
  }, [containerWidth, from, to, hasVerticalSubHeaders, rowHeaderWidth]);
};
